import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Building2, Calendar, Briefcase, Layers } from "lucide-react";
import { PROJECTS_DATA } from "./ProjekteReferenz";
import { useState } from "react";

// ============ DESIGN SYSTEM ============
const GRID = 8;
const VISUAL_GRID = 40;
const sp = (n) => `${n * GRID}px`;

// ============ GRID PATTERN ============
function GridPattern({ dark = true }) {
  const color = dark 
    ? "var(--tga-border)" 
    : "rgba(0,0,0,0.035)";
  
  return (
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `
          linear-gradient(to right, ${color} 1px, transparent 1px),
          linear-gradient(to bottom, ${color} 1px, transparent 1px)
        `,
        backgroundSize: `${VISUAL_GRID}px ${VISUAL_GRID}px`,
      }}
    />
  );
}

// ============ LOGO ============
function TGAoneLogo() {
  return (
    <a 
      href="/"
      className="detail-logo-link"
      style={{ 
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none'
      }}
    >
      <img 
        src="/tgaone_black.svg"
        alt="TGAone" 
        className="detail-logo"
        style={{ 
          height: '80px',
          width: 'auto',
          objectFit: 'contain',
          display: 'block',
          transition: 'opacity 0.2s ease'
        }}
      />
      <style>{`
        .detail-logo-link:hover .detail-logo {
          opacity: 0.9;
        }
        .detail-logo {
          height: 80px !important;
        }
        @media (max-width: 1024px) {
          .detail-logo {
            height: 64px !important;
          }
        }
        @media (max-width: 768px) {
          .detail-logo {
            height: 52px !important;
          }
        }
      `}</style>
    </a>
  );
}

// ============ TECH DATA ITEM ============
function TechDataItem({ icon: Icon, label, value }) {
  return (
    <div 
      className="flex items-start gap-4"
      style={{ paddingBottom: sp(3), borderBottom: '1px solid #e5e5e5' }}
    >
      <div 
        className="flex-shrink-0 bg-[#f5f5f5] rounded flex items-center justify-center"
        style={{ width: sp(6), height: sp(6) }}
      >
        <Icon className="w-5 h-5 text-[#404040]" />
      </div>
      <div>
        <div 
          className="text-[#6b7280] font-medium"
          style={{ fontSize: '13px', marginBottom: '2px' }}
        >
          {label}
        </div>
        <div 
          className="text-[#1a1a1a] font-semibold"
          style={{ fontSize: '15px' }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// ============ GALLERY ============
function Gallery({ images, title }) {
  const [activeIndex, setActiveIndex] = useState(0);
  
  return (
    <div>
      {/* Main Image */}
      <div 
        className="relative overflow-hidden bg-[#f5f5f5] mb-4"
        style={{ aspectRatio: '16/9' }}
      >
        <img 
          src={images[activeIndex]}
          alt={`${title} - Bild ${activeIndex + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-3">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`relative overflow-hidden transition-opacity ${
                idx === activeIndex ? 'ring-2 ring-black' : 'opacity-60 hover:opacity-100'
              }`}
              style={{ width: '80px', height: '60px' }}
            >
              <img 
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ MAIN COMPONENT ============
export default function ProjektDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Find project by slug
  const project = PROJECTS_DATA.find(p => p.slug === slug);
  
  // Find next/prev projects
  const currentIndex = PROJECTS_DATA.findIndex(p => p.slug === slug);
  const prevProject = currentIndex > 0 ? PROJECTS_DATA[currentIndex - 1] : null;
  const nextProject = currentIndex < PROJECTS_DATA.length - 1 ? PROJECTS_DATA[currentIndex + 1] : null;
  
  if (!project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1a1a1a] mb-4">Projekt nicht gefunden</h1>
          <Link 
            to="/projekte" 
            className="text-[#6b7280] hover:text-[#1a1a1a] inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Inter',system-ui,sans-serif]">
      {/* ============ NAVIGATION ============ */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
        style={{ height: sp(14) }}
      >
        <div 
          className="mx-auto h-full"
          style={{ 
            maxWidth: '1120px',
            paddingLeft: sp(6),
            paddingRight: sp(6)
          }}
        >
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center h-full">
              <TGAoneLogo />
            </div>
            
            <div className="hidden md:flex items-center" style={{ gap: sp(4) }}>
              <Link 
                to="/"
                className="text-sm font-medium transition-colors text-[#6b7280] hover:text-[#1a1a1a]"
              >
                Startseite
              </Link>
              <Link 
                to="/projekte"
                className="text-sm font-medium transition-colors text-[#1a1a1a]"
              >
                Projekte
              </Link>
              <div 
                className="bg-[#e5e5e5]" 
                style={{ width: '1px', height: sp(3) }} 
              />
              <Link 
                to="/login"
                className="text-sm font-medium transition-colors text-[#6b7280] hover:text-[#1a1a1a]"
              >
                Interner Bereich
              </Link>
            </div>
            
            <Link 
              to="/login"
              className="md:hidden text-sm font-medium text-[#6b7280]"
            >
              Login
            </Link>
          </div>
        </div>
      </nav>
      
      {/* ============ BACK LINK ============ */}
      <div 
        className="bg-[#fafafa] border-b border-[#e5e5e5]"
        style={{ paddingTop: sp(16) }}
      >
        <div 
          className="mx-auto"
          style={{ 
            maxWidth: '1120px',
            paddingLeft: sp(6),
            paddingRight: sp(6),
            paddingTop: sp(4),
            paddingBottom: sp(4)
          }}
        >
          <Link 
            to="/projekte"
            className="inline-flex items-center gap-2 text-[#6b7280] hover:text-[#1a1a1a] transition-colors"
            style={{ fontSize: '14px' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Projekte
          </Link>
        </div>
      </div>
      
      {/* ============ HERO IMAGE ============ */}
      <div 
        className="w-full bg-black"
        style={{ height: '50vh', minHeight: '400px' }}
      >
        <img 
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover opacity-80"
        />
      </div>
      
      {/* ============ CONTENT ============ */}
      <section 
        className="relative bg-white"
        style={{ 
          paddingTop: sp(10),
          paddingBottom: sp(16)
        }}
      >
        <div 
          className="mx-auto"
          style={{ 
            maxWidth: '1120px',
            paddingLeft: sp(6),
            paddingRight: sp(6)
          }}
        >
          <div className="grid md:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="md:col-span-2">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {project.tags.map((tag, idx) => (
                  <span 
                    key={idx}
                    className="bg-black text-white font-medium"
                    style={{ 
                      fontSize: '12px', 
                      padding: '4px 12px'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              {/* Title */}
              <h1 
                className="font-bold text-[#1a1a1a] tracking-tight"
                style={{ 
                  fontSize: '36px', 
                  lineHeight: '1.2',
                  marginBottom: sp(4)
                }}
              >
                {project.title}
              </h1>
              
              {/* Description */}
              <p 
                className="text-[#6b7280]"
                style={{ 
                  fontSize: '17px', 
                  lineHeight: '1.7',
                  marginBottom: sp(8)
                }}
              >
                {project.description}
              </p>
              
              {/* Leistungsumfang */}
              <div style={{ marginBottom: sp(8) }}>
                <h2 
                  className="font-bold text-[#1a1a1a]"
                  style={{ 
                    fontSize: '20px', 
                    marginBottom: sp(4)
                  }}
                >
                  Leistungsumfang
                </h2>
                
                <ul className="space-y-3">
                  {project.leistungen.map((item, idx) => (
                    <li 
                      key={idx}
                      className="flex items-start gap-3 text-[#404040]"
                      style={{ fontSize: '15px', lineHeight: '1.6' }}
                    >
                      <span 
                        className="w-1.5 h-1.5 rounded-full bg-black flex-shrink-0"
                        style={{ marginTop: '8px' }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Gallery */}
              <div>
                <h2 
                  className="font-bold text-[#1a1a1a]"
                  style={{ 
                    fontSize: '20px', 
                    marginBottom: sp(4)
                  }}
                >
                  Bildergalerie
                </h2>
                
                <Gallery images={project.gallery} title={project.title} />
              </div>
            </div>
            
            {/* Right Column - Tech Data */}
            <div>
              <div 
                className="bg-[#fafafa] sticky"
                style={{ 
                  padding: sp(4),
                  top: sp(18)
                }}
              >
                <h2 
                  className="font-bold text-[#1a1a1a]"
                  style={{ 
                    fontSize: '16px', 
                    marginBottom: sp(4)
                  }}
                >
                  Technische Daten
                </h2>
                
                <div className="space-y-4">
                  <TechDataItem 
                    icon={Building2}
                    label="Auftraggeber"
                    value={project.technischeDaten.auftraggeber}
                  />
                  <TechDataItem 
                    icon={Briefcase}
                    label="Projektvolumen"
                    value={project.technischeDaten.projektvolumen}
                  />
                  <TechDataItem 
                    icon={Calendar}
                    label="Zeitraum"
                    value={project.technischeDaten.zeitraum}
                  />
                  <TechDataItem 
                    icon={Layers}
                    label="Leistungsphase"
                    value={project.technischeDaten.leistungsphase}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ============ PROJECT NAVIGATION ============ */}
      <section 
        className="relative bg-[#fafafa] border-t border-[#e5e5e5]"
        style={{ 
          paddingTop: sp(8),
          paddingBottom: sp(8)
        }}
      >
        <div 
          className="mx-auto"
          style={{ 
            maxWidth: '1120px',
            paddingLeft: sp(6),
            paddingRight: sp(6)
          }}
        >
          <div className="flex justify-between items-center">
            {prevProject ? (
              <Link 
                to={`/projekte/${prevProject.slug}`}
                className="flex items-center gap-3 text-[#6b7280] hover:text-[#1a1a1a] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <div className="hidden sm:block">
                  <div className="text-xs text-[#999] mb-1">Vorheriges Projekt</div>
                  <div className="font-medium text-sm">{prevProject.title}</div>
                </div>
              </Link>
            ) : (
              <div />
            )}
            
            <Link 
              to="/projekte"
              className="text-[#6b7280] hover:text-[#1a1a1a] transition-colors text-sm font-medium"
            >
              Alle Projekte
            </Link>
            
            {nextProject ? (
              <Link 
                to={`/projekte/${nextProject.slug}`}
                className="flex items-center gap-3 text-[#6b7280] hover:text-[#1a1a1a] transition-colors text-right"
              >
                <div className="hidden sm:block">
                  <div className="text-xs text-[#999] mb-1">Nächstes Projekt</div>
                  <div className="font-medium text-sm">{nextProject.title}</div>
                </div>
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>
      
      {/* ============ CTA SECTION ============ */}
      <section 
        className="relative bg-black overflow-hidden"
        style={{ 
          paddingTop: sp(12),
          paddingBottom: sp(12)
        }}
      >
        <GridPattern dark={true} />
        
        <div 
          className="mx-auto relative z-10 text-center"
          style={{ 
            maxWidth: '1120px',
            paddingLeft: sp(6),
            paddingRight: sp(6)
          }}
        >
          <h2 
            className="font-bold text-white tracking-tight"
            style={{ 
              fontSize: '32px', 
              lineHeight: sp(6),
              marginBottom: sp(3)
            }}
          >
            Ihr Projekt ist unser nächstes.
          </h2>
          
          <p 
            className="text-white/50 mx-auto"
            style={{ 
              fontSize: '16px', 
              lineHeight: sp(3),
              marginBottom: sp(6),
              maxWidth: '500px'
            }}
          >
            Sprechen Sie uns an – wir freuen uns auf Ihre Anfrage.
          </p>
          
          <Link 
            to="/#kontakt"
            className="inline-flex items-center justify-center bg-white text-black font-semibold hover:bg-white/90 transition-colors"
            style={{ 
              height: sp(6),
              paddingLeft: sp(4),
              paddingRight: sp(4),
              gap: sp(1),
              fontSize: '14px'
            }}
          >
            Kontakt aufnehmen
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      
      {/* ============ FOOTER ============ */}
      <footer 
        className="bg-black border-t border-white/5 relative overflow-hidden"
        style={{ 
          paddingTop: sp(4),
          paddingBottom: sp(4)
        }}
      >
        <GridPattern dark={true} />
        <div 
          className="mx-auto relative z-10"
          style={{ 
            maxWidth: '1120px',
            paddingLeft: sp(6),
            paddingRight: sp(6)
          }}
        >
          <div 
            className="flex flex-col md:flex-row items-center justify-between"
            style={{ gap: sp(3) }}
          >
            <div className="flex items-center">
              <a href="/" className="hover:opacity-90 transition-opacity">
                <img 
                  src="/tgaone_white.svg" 
                  alt="TGAone" 
                  style={{ height: '56px', width: 'auto' }}
                />
              </a>
            </div>
            
            <div 
              className="flex items-center text-sm"
              style={{ gap: sp(4), height: sp(3) }}
            >
              <span className="text-white/30">© 2026 TGAone</span>
              <div className="w-px bg-white/10 h-4" />
              <Link 
                to="/login"
                className="text-white/30 hover:text-white/60 transition-colors"
              >
                Interner Bereich
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
